import * as actions from './authActions';
import { getMFAOptionsFromCallbacks } from '../../lib/mfaHelpers';

const authReducers = {
  [actions.AUTH_SET_STEP]: (state, { payload }) => {
    state.step = payload;
    return state;
  },

  [actions.AUTH_SELECT_MFA]: (state, { payload }) => {
    state.selectedMFA = payload;
    return state;
  },

  [actions.AUTH_SET_OTP]: (state, { payload }) => {
    state.otp = payload;
    if (state.fieldErrors?.otp) {
      state.fieldErrors = { ...state.fieldErrors, otp: null };
    }
    return state;
  },

  [actions.AUTH_UPDATE_STATE]: (state, { payload }) => ({
    ...state,
    ...payload,
  }),

  [actions.AUTH_CLEAR_STATE]: () => ({}),

  [actions.AUTH_SET_USERNAME]: (state, { payload }) => {
    state.username = payload;
    if (state.fieldErrors?.email) {
      state.fieldErrors = { ...state.fieldErrors, email: null };
    }
    return state;
  },

  [actions.AUTH_SET_PASSWORD]: (state, { payload }) => {
    state.password = payload;
    if (state.fieldErrors?.password) {
      state.fieldErrors = { ...state.fieldErrors, password: null };
    }
    return state;
  },

  [actions.AUTH_SET_USER_DETAILS]: (state, { payload }) => {
    state.userDetails = { ...state.userDetails, ...payload };
    return state;
  },

  [actions.AUTH_CLEAR_USER_DETAILS]: (state) => {
    state.userDetails = {
      firstname: '',
      lastname: '',
      address: '',
      userEmail: '',
      phoneNumber: '',
    };
    return state;
  },

  [actions.AUTH_SIGN_IN_REQUEST]: state => {
    state.isLoading = true;
    state.error = null;
    state.fieldErrors = {};
    state.lockoutModalOpen = false;
    return state;
  },

  [actions.AUTH_SIGN_IN_SUCCESS]: (state, { payload }) => {    
    state.isLoading = false;
    state.isAuthenticated = Boolean(payload?.tokenId);
    state.tokenId = payload?.tokenId || null;
    state.error = null;
    state.fieldErrors = {};
    state.password = '';
    state.authenticatePayload = payload || null;
    state.mfaOptions = getMFAOptionsFromCallbacks(payload?.callbacks || []);
    return state;
  },

  [actions.AUTH_VERIFY_OTP_REQUEST]: state => {
    state.isLoading = true;
    state.error = null;
    return state;
  },

  // Final post-auth cleanup/state finalize after OTP+authorize+consent checks complete.
  [actions.AUTH_POST_AUTH_SUCCESS]: (state, { payload }) => {
    state.isLoading = false;
    state.isAuthenticated = true;
    state.tokenId = payload?.tokenId || null;
    state.password = '';
    state.continuationPassword = '';
    state.otp = '';
    return state;
  },

  [actions.AUTH_VERIFY_OTP_FAILED]: (state, { payload }) => {
    state.isLoading = false;
    state.fieldErrors = payload.fieldErrors || {};
    state.error = null;
    if (payload.fieldErrors?.otp) {
      state.otp = '';
    }
    return state;
  },

  [actions.AUTH_ACCEPT_CONSENT_REQUEST]: (state) => {
    state.isLoading = true;
    state.error = null;
    return state;
  },

  [actions.AUTH_ACCEPT_CONSENT_SUCCESS]: state => {
    state.isLoading = false;
    state.error = null;
    return state;
  },

  [actions.AUTH_ACCEPT_CONSENT_FAILED]: (state, { payload }) => {
    state.isLoading = false;
    state.error = payload;
    return state;
  },

  [actions.AUTH_SERVER_ERROR_MODAL_OPEN]: (state, { payload }) => {
    // Timeout modal takes priority — don't overwrite it with a server error modal
    if (state.timeoutModalOpen) return state;
    state.isLoading = false;
    state.serverErrorModalOpen = true;
    state.uniqueIdentifier = payload?.uniqueIdentifier || null;
    state.error = null;
    return state;
  },

  [actions.AUTH_SERVER_ERROR_MODAL_CLOSE]: state => {
    state.serverErrorModalOpen = false;
    state.uniqueIdentifier = null;
    return state;
  },

  [actions.AUTH_TIMEOUT_MODAL_OPEN]: state => {
    // Don't show timeout modal if server error modal is already open
    // Server error takes priority
    if (state.serverErrorModalOpen) {
      return state;
    }
    state.timeoutModalOpen = true;
    return state;
  },

  [actions.AUTH_TIMEOUT_MODAL_CLOSE]: state => {
    state.timeoutModalOpen = false;
    return state;
  },

  [actions.AUTH_SIGN_IN_FAILED]: (state, { payload }) => {
    state.isLoading = false;
    // If payload has fieldErrors (inline errors), store them; otherwise store as generic error
    if (payload?.fieldErrors) {
      state.fieldErrors = payload.fieldErrors;
      state.error = null;
    } else {
      state.error = payload;
      state.fieldErrors = {};
    }
    state.password = '';
    return state;
  },

  [actions.AUTH_SIGN_IN_DIFFERENT]: state => {
    state.step = actions.AUTH_STEPS.SIGN_IN;
    state.continuationUsername = '';
    state.continuationPassword = '';
    state.authenticatePayload = null;
    return state;
  },

  [actions.AUTH_GET_MFA_CHOICES_REQUEST]: state => {
    state.isLoading = true;
    return state;
  },

  [actions.AUTH_GET_MFA_CHOICES_SUCCESS]: (state, { payload }) => {
    state.isLoading = false;
    state.mfaOptions = payload;
    return state;
  },

  [actions.AUTH_GET_MFA_CHOICES_FAILED]: (state, { payload }) => {
    state.isLoading = false;
    state.mfaOptions = [];
    state.error = payload || state.error;
    return state;
  },

  [actions.AUTH_LOCKOUT_DETECTED]: state => {
    state.lockoutModalOpen = true;
    state.isLoading = false;
    state.step = actions.AUTH_STEPS.SIGN_IN;
    return state;
  },

  [actions.AUTH_CLOSE_LOCKOUT_MODAL]: state => {
    state.lockoutModalOpen = false;
    state.isLoading = false;
    state.error = null;
    state.step = actions.AUTH_STEPS.SIGN_IN;
    
    // Clear form inputs
    state.username = '';
    state.password = '';
    state.otp = '';
    state.selectedMFA = null;
    state.mfaOptions = [];
    
    // Clear continuation state
    state.continuationUsername = '';
    state.continuationPassword = '';
    
    // Clear authentication data
    state.tokenId = null;
    state.authenticatePayload = null;
    
    return state;
  },
  // Global reset on back button
  RESET_ALL: () => ({}),
};

export default authReducers;
